import React, { FC, useState, useEffect } from "react";
import {
  Container,
  Paper,
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableCell,
  TableRow,
} from "@mui/material";
import { makeStyles } from '@material-ui/core/styles';
import { CircularProgressMolecule } from "../components/molecules/CircularProgressMolecule";
import { VisualizedMenus } from "../components/VisualizedMenus";
import { Toolbar } from "../components/FooterToolbar";
import { FeedbackBar } from "../components/molecules/FeedbackBar";
import { getCredit, getCreditDataRef, useAuth0Token } from "../common/http-requests";
import {
  creditRequestType,
  creditResponseType,
  creditDataRefResponseType,
  creditPs8ResponseType,
} from "../common/type";
import { useTranslation } from 'react-i18next';
import { formatSemester } from "../common/util";
import { themeColors } from "../common/colors";
import { useUserId } from "../hooks/useStoreGetters";

// Material-UIのスタイル設定
const useStyles = makeStyles({
  chartContainer: {
    position: 'relative',
    marginTop: '60px',
    padding: '20px',
    overflow: 'hidden'
  },
  tableHeadStyle: {
    textAlign: "center",
    borderRight: "solid 1px #fff",
    lineHeight: "1rem",
    padding: "10px 2px",
  },
  // tableHeadFixedHeightStyleは、stickyHeaderに対応するため。高さが固定でないとヘッダーが崩れてしまうため
  // tableHeadStyleを使っているところは、動作を確認して画面幅を変更しても高さが変わらないため指定しない
  tableHeadFixedHeightStyle: {
    textAlign: "center",
    borderRight: "solid 1px #fff",
    lineHeight: "1rem",
    padding: "0 2px",
    height: "69px",
  },
  tableCellStyle: {
    position: 'relative',
    padding: "0 2px",
    textAlign: 'center',
    overflow: "hidden",
  },
  tableCellRefStyle: {
    border: 'solid 1px #e2e2e2',
    boxSizing: 'border-box',
  },
  tableCellPsStyle: {
    border: 'solid 1px #bbcddd',
    boxSizing: 'border-box',
  },
  tableCellVertical: {
    writingMode: 'vertical-rl',
    textOrientation: 'upright',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-start',
    flexDirection: 'row',
    flexWrap: 'wrap',
    alignContent: 'center',
    width: '100%',
    height: '22%',
  },
});

// MEMO: 鳴門パースペクティブの各観点毎の取得成績値である ps_credits の要素数は常に26個。
const DefaultCreditDataRef: creditDataRefResponseType = {
  data_ref: [],
};

const CreditDataRefPage: FC = () => {
  const { t } = useTranslation();
  const [isSending, setIsSending] = useState(true);
  const { getToken } = useAuth0Token();
  const userId = useUserId();
  const classes = useStyles();

  // データ例
  //   "data_ref": [
  //     { 'earned_period': 2019, 'earned_semester_code': '1', 'subject_name': '日本国憲法', 'credit_score': 100, 'ps_credits': [1.0, ..., 0.0] },
  //     ...
  //     ]
  const [creditDataRefData, setCreditDataRefData] = useState<creditDataRefResponseType>(DefaultCreditDataRef);
  const [creditLatestData, setCreditLatestData] = useState<creditPs8ResponseType>({
    ps8_credit_1: 0.0,
    ps8_credit_2: 0.0,
    ps8_credit_3: 0.0,
    ps8_credit_4: 0.0,
    ps8_credit_5: 0.0,
    ps8_credit_6: 0.0,
    ps8_credit_7: 0.0,
    ps8_credit_8: 0.0,
  });

  const perspectiveList = Array.from({ length: 26 }, (_, i) => ({
    id: String(i + 1).padStart(2, "0"),
    label: t(`common.perspective_${i + 1}`)
  }));
  const pl = perspectiveList;
  const tableHeadListCS = [
    { label: t("common.period"), width: "4vw", color: "white", backgroundColor: themeColors[3] },
    { label: t("common.semester"), width: "4vw", color: "white", backgroundColor: themeColors[3] },
    { label: t("common.subjectName"), width: "20vw", color: "white", backgroundColor: themeColors[3] },
    { label: t("common.credit"), width: "3vw", color: "white", backgroundColor: themeColors[3] },
    { label: t("common.creditScore"), width: "4vw", color: "white", backgroundColor: themeColors[3] },
  ]
  const tableHeadListPers = [
    { label: t("common.ps8_1"), width: "10vw", color: "white", colSpan: 3, backgroundColor: themeColors[0], perspective: [pl[0], pl[1], pl[2]] },
    { label: t("common.ps8_2"), width: "10vw", color: "white", colSpan: 3, backgroundColor: themeColors[0], perspective: [pl[3], pl[4], pl[5]] },
    { label: t("common.ps8_3"), width: "10vw", color: "white", colSpan: 4, backgroundColor: themeColors[0], perspective: [pl[6], pl[7], pl[8], pl[9]] },
    { label: t("common.ps8_4"), width: "10vw", color: "white", colSpan: 2, backgroundColor: themeColors[0], perspective: [pl[10], pl[11]] },
    { label: t("common.ps8_5"), width: "10vw", color: "white", colSpan: 4, backgroundColor: themeColors[0], perspective: [pl[12], pl[13], pl[14], pl[15]] },
    { label: t("common.ps8_6"), width: "10vw", color: "white", colSpan: 3, backgroundColor: themeColors[0], perspective: [pl[16], pl[17], pl[18]] },
    { label: t("common.ps8_7"), width: "10vw", color: "white", colSpan: 5, backgroundColor: themeColors[0], perspective: [pl[19], pl[20], pl[21], pl[22], pl[23]] },
    { label: t("common.ps8_8"), width: "10vw", color: "white", colSpan: 2, backgroundColor: themeColors[0], perspective: [pl[24], pl[25]] },
  ]
  const tableBodyTotalRowList = [
    { colSpan: 3, total: creditLatestData.ps8_credit_1 },
    { colSpan: 3, total: creditLatestData.ps8_credit_2 },
    { colSpan: 4, total: creditLatestData.ps8_credit_3 },
    { colSpan: 2, total: creditLatestData.ps8_credit_4 },
    { colSpan: 4, total: creditLatestData.ps8_credit_5 },
    { colSpan: 3, total: creditLatestData.ps8_credit_6 },
    { colSpan: 5, total: creditLatestData.ps8_credit_7 },
    { colSpan: 2, total: creditLatestData.ps8_credit_8 },
  ]

  // api取得失敗時のアラート
  const [feedbackInfo, setFeedbackInfo] = useState({
    open: false,
    text: "",
    type: "success" as "error" | "success" | "warning" | "info",
    autoHideDuration: null,
  });

  // clickawayした際、api取得失敗時のアラートを行わない。
  const handleClose = (event?: React.SyntheticEvent, reason?: string) => {
    if (reason === "clickaway") {
      return;
    }
    setFeedbackInfo({ ...feedbackInfo, open: false });
  };

  // 以下は、記録一覧画面のコードを流用した。
  // Phase1と異なり、画面遷移が必要ではないため、location（uselocation）は使用しない。
  // React18(2022年)より、strictMode（javascriptのコードを通常より厳しくエラーチェックできるモード）において
  // オフスクリーンから再度表示されることで、useEffectが２回発火（キック）される仕様変更となっため、1回に制限するよう変更した。
  // また、以下のコードの})();の();は、定義した関数を即時実行している。
  // 最後にreturn文にて定義された関数は、クリーンアップ関数であり、リスナー、オブザーバー等のオブジェクトを設定した場合、
  // レンダリング時に毎回インスタンスを作成されるため、メモリーリーク及びメモリーの圧迫を抑止するための関数。
  // useEffectの依存配列（第2引数）は、通常、空リストもしくは、具体的な変数のリストを登録するのが一般的であり、
  // 定義しない場合は、FCのstate, propsのうちいずれかが更新される度に実行されてしまうため、通常、定義しないことはない。
  let alreadyFiredOnce = false;
  useEffect(() => {
    (async () => {
      if (!alreadyFiredOnce) {
        await getCreditData(userId);
      }
    })();
    return () => {
      alreadyFiredOnce = true;
    };
  }, []);

  const getCreditData = async (
    login_user_id: number, // userResponseType.id の値
  ) => {
    setIsSending(true);
    try {
      const requestParams: creditRequestType = {
        login_user_id,
      };
      const data: creditResponseType = await getCredit(
        await getToken(),
        requestParams
      );
      const dataRefData: creditDataRefResponseType = await getCreditDataRef(
        await getToken(),
        requestParams
      );
      setCreditLatestData(data.latest_credits);
      setCreditDataRefData(dataRefData);
    } catch {
      setFeedbackInfo({
        ...feedbackInfo,
        open: true,
        text: t("msg.cannotLoadData"),
        type: "error",
      });
    } finally {
      setIsSending(false);
    }
  };

  return (
    <>
      <Container maxWidth="xl">
        {isSending ? (
          <>
            <CircularProgressMolecule displayLabel={t("msg.loading")} />
          </>
        ) : (
          <>
            <VisualizedMenus />
            {feedbackInfo.open &&
              <FeedbackBar
                feedbackInfo={feedbackInfo}
                handleClose={handleClose}
              ></FeedbackBar>
            }
            <Paper className={classes.chartContainer}>
              <TableContainer sx={{ maxHeight: 700 }}>
                <Table aria-label="simple table" stickyHeader>
                  <TableHead key="table-head">
                    <TableRow>
                      <TableCell
                        className={classes.tableHeadStyle}
                        sx={{ color: 'white', backgroundColor: themeColors[3] }}
                        colSpan={5}
                      >{t("common.subjectName")}</TableCell>
                      <TableCell sx={{ width: '2px', padding: '0' }} />
                      <TableCell
                        className={classes.tableHeadStyle}
                        sx={{ color: 'white', backgroundColor: themeColors[0] }}
                        colSpan={26}
                      >{t("common.perspective")}</TableCell>
                    </TableRow>
                    <TableRow>
                      {tableHeadListCS.map((item: any, index: number) => (
                        <TableCell
                          key={`${item.label}-${index}`}
                          className={classes.tableHeadStyle}
                          sx={{
                            width: item.width,
                            color: item.color,
                            backgroundColor: item.backgroundColor,
                            top: '37px' // stickyHeaderの位置調整の値
                          }}
                          rowSpan={3}
                        >{item.label}
                        </TableCell>
                      ))}
                      <TableCell sx={{ width: '2px', padding: '0' }} />
                      {tableHeadListPers.map((item: any, index: number) => (
                        <TableCell
                          key={`${item.label}-${index}`}
                          className={classes.tableHeadFixedHeightStyle}
                          sx={{
                            width: item.width,
                            color: item.color,
                            backgroundColor: item.backgroundColor,
                            fontSize: '.8rem',
                            top: '37px' // stickyHeaderの位置調整の値
                          }}
                          colSpan={item.colSpan}
                        >{item.label}
                        </TableCell>
                      ))}
                    </TableRow>
                    <TableRow>
                      <TableCell sx={{ width: '2px', padding: '0' }} />
                      {tableHeadListPers.map((pers: any, persindex: number) => (
                        pers.perspective.map((item: any, index: number) => (
                          <TableCell
                            key={`${item.label}-${index}`}
                            className={classes.tableHeadStyle}
                            sx={{
                              width: '1vw',
                              color: pers.color,
                              backgroundColor: pers.backgroundColor,
                              top: '106px' // stickyHeaderの位置調整の値
                            }}
                          >{item.id}
                          </TableCell>
                        ))
                      ))}
                    </TableRow>
                    <TableRow>
                      <TableCell sx={{ width: '2px', padding: '0' }} />
                      {tableHeadListPers.map((pers: any, persindex: number) => (
                        pers.perspective.map((item: any, index: number) => (
                          <TableCell
                            key={`${item.label}-${index}`}
                            className={classes.tableHeadStyle}
                            sx={{
                              width: '1vw',
                              color: pers.color,
                              backgroundColor: pers.backgroundColor,
                              fontSize: '.8rem',
                              top: '143px' // stickyHeaderの位置調整の値
                            }}
                          ><div className={classes.tableCellVertical}>{item.label}</div>
                          </TableCell>
                        ))
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody key="table-body">
                    {creditDataRefData.data_ref.map((item: any, index: number) => (
                      <TableRow key={`table-body-row-${index}`}>
                        <React.Fragment key={`${item.subject_name}-${index}`}>
                          <TableCell className={`${classes.tableCellStyle} ${classes.tableCellRefStyle}`}>{item.earned_period}</TableCell>
                          <TableCell className={`${classes.tableCellStyle} ${classes.tableCellRefStyle}`}>{formatSemester(item.earned_semester_code,t)}</TableCell>
                          <TableCell className={`${classes.tableCellStyle} ${classes.tableCellRefStyle}`}>{item.subject_name}</TableCell>
                          <TableCell className={`${classes.tableCellStyle} ${classes.tableCellRefStyle}`}>{item.credit}</TableCell>
                          <TableCell className={`${classes.tableCellStyle} ${classes.tableCellRefStyle}`}>{item.credit_score ?? "－"}</TableCell>
                          <TableCell sx={{ width: '2px', padding: '0' }} />
                          {item.ps_credits.map((ps: any, psindex: number) => (
                            <TableCell
                              key={`ps-${psindex}`}
                              className={`${classes.tableCellStyle} ${classes.tableCellPsStyle}`}
                              sx={{ color: themeColors[0] }}
                            >{ps}</TableCell>
                          ))}
                        </React.Fragment>
                      </TableRow>
                    ))}
                    <TableRow>
                      <TableCell
                        className={classes.tableHeadStyle}
                        sx={{ color: 'white', backgroundColor: themeColors[3] }}
                        colSpan={5}
                      >合計</TableCell>
                      <TableCell sx={{ width: '2px', padding: '0' }} />
                      {tableBodyTotalRowList.map((item: { colSpan: number; total: number }, index: number) => (
                        <TableCell
                          key={`table-body-total-row-${index}`}
                          className={classes.tableHeadStyle}
                          sx={{ color: 'white', backgroundColor: themeColors[0] }}
                          colSpan={item.colSpan}
                        >{item.total}</TableCell>
                      )
                      )}
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          </>
        )}
        <Toolbar />
      </Container>
    </>
  );
};

export default CreditDataRefPage;
